import { useState } from "react";
import { ExtractedInsight } from "./ExpertInterview";

interface Props {
  insights: ExtractedInsight[];
  expertName: string;
  duration: number;
  onBack: () => void;
}

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  ready: { bg: "rgba(16,185,129,0.1)", text: "#059669", label: "Ready" },
  needs_review: { bg: "rgba(245,158,11,0.1)", text: "#d97706", label: "Review" },
  excluded: { bg: "rgba(239,68,68,0.1)", text: "#dc2626", label: "Excluded" },
};

const typeColors: Record<string, { bg: string; text: string }> = {
  variable: { bg: "rgba(99,102,241,0.1)", text: "#6366f1" },
  belief: { bg: "rgba(16,185,129,0.1)", text: "#059669" },
  threshold: { bg: "rgba(245,158,11,0.1)", text: "#d97706" },
};

function getStatus(ins: ExtractedInsight): "ready" | "needs_review" | "excluded" {
  return (ins.confidence ?? 0) >= 0.7 ? "ready" : "needs_review";
}

function getVarName(ins: ExtractedInsight, idx: number): string {
  return ins.variable_name || ins.text
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .split(/\s+/)
    .slice(0, 4)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("_") || `Variable_${idx + 1}`;
}

function exportCSV(insights: ExtractedInsight[], expertName: string) {
  const headers = [
    "Variable", "Type", "Description", "Predictive Power", "Certainty",
    "Prior Mean", "Prior Std", "Mechanism", "Thresholds", "Boundary Conditions",
    "Verbatim Quote", "Expert", "Status"
  ];
  const rows = insights.map((ins, i) => [
    getVarName(ins, i),
    ins.type,
    `"${(ins.text || "").replace(/"/g, '""')}"`,
    ins.predictive_power != null ? ins.predictive_power.toFixed(3) : "",
    ins.confidence != null ? ins.confidence.toFixed(3) : "",
    ins.prior_mean != null ? ins.prior_mean.toFixed(4) : "",
    ins.prior_std != null ? ins.prior_std.toFixed(4) : "",
    `"${(ins.mechanism || "").replace(/"/g, '""')}"`,
    `"${(ins.thresholds || []).join("; ").replace(/"/g, '""')}"`,
    `"${(ins.boundary_conditions || []).join("; ").replace(/"/g, '""')}"`,
    `"${(ins.source_quote || "").replace(/"/g, '""')}"`,
    expertName,
    getStatus(ins),
  ]);
  const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `interview_${expertName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportJSON(insights: ExtractedInsight[], expertName: string) {
  const data = {
    expert: expertName,
    exported_at: new Date().toISOString(),
    variables: insights.map((ins, i) => ({
      variable_name: getVarName(ins, i),
      type: ins.type,
      description: ins.text,
      predictive_power: ins.predictive_power ?? null,
      certainty: ins.confidence ?? null,
      prior: {
        distribution: "Normal",
        mean: ins.prior_mean ?? null,
        std: ins.prior_std ?? null,
      },
      mechanism: ins.mechanism || null,
      thresholds: ins.thresholds || [],
      boundary_conditions: ins.boundary_conditions || [],
      verbatim_quote: ins.source_quote || null,
      status: getStatus(ins),
    })),
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `interview_${expertName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function InterviewResults({ insights, expertName, duration, onBack }: Props) {
  const [expandedVar, setExpandedVar] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<string>("overview");
  const [statuses, setStatuses] = useState<Record<number, "ready" | "needs_review" | "excluded">>(() => {
    const s: Record<number, "ready" | "needs_review" | "excluded"> = {};
    insights.forEach((ins, i) => { s[i] = getStatus(ins); });
    return s;
  });
  const [showExportMenu, setShowExportMenu] = useState(false);

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}m ${sec}s`;
  };

  const cycleStatus = (idx: number) => {
    const order: ("ready" | "needs_review" | "excluded")[] = ["ready", "needs_review", "excluded"];
    setStatuses(prev => ({
      ...prev,
      [idx]: order[(order.indexOf(prev[idx]) + 1) % order.length],
    }));
  };

  const readyCount = Object.values(statuses).filter(s => s === "ready").length;
  const reviewCount = Object.values(statuses).filter(s => s === "needs_review").length;
  const avgCertainty = insights.reduce((a, b) => a + (b.confidence || 0), 0) / Math.max(insights.length, 1);
  const avgPredictive = insights.reduce((a, b) => a + (b.predictive_power ?? 0), 0) / Math.max(insights.length, 1);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <style>{`
        @keyframes fadeSlideIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      {/* Header */}
      <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 6px" }}>Interview Results</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="tag" style={{ fontSize: 11 }}>{expertName}</span>
              <span style={{ fontSize: 11, color: "rgba(0,0,0,0.4)" }}>{formatDuration(duration)}</span>
              <span style={{ fontSize: 11, color: "rgba(0,0,0,0.4)" }}>{insights.length} variables extracted</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={onBack} className="btn btn-outline" style={{ height: 36, padding: "0 16px", fontSize: 12 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </button>
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="btn btn-filled" style={{ height: 36, padding: "0 20px", fontSize: 12 }}
              >
                Export
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </button>
              {showExportMenu && (
                <div style={{
                  position: "absolute", top: 40, right: 0, background: "#fff", borderRadius: 10,
                  border: "1px solid rgba(0,0,0,0.1)", boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                  padding: 4, zIndex: 10, minWidth: 140,
                }}>
                  <button
                    onClick={() => { exportCSV(insights, expertName); setShowExportMenu(false); }}
                    style={{
                      display: "block", width: "100%", padding: "8px 12px", border: "none", background: "transparent",
                      fontSize: 12, textAlign: "left", cursor: "pointer", borderRadius: 6, fontFamily: "inherit",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.04)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    Export CSV
                  </button>
                  <button
                    onClick={() => { exportJSON(insights, expertName); setShowExportMenu(false); }}
                    style={{
                      display: "block", width: "100%", padding: "8px 12px", border: "none", background: "transparent",
                      fontSize: 12, textAlign: "left", cursor: "pointer", borderRadius: 6, fontFamily: "inherit",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.04)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    Export JSON
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ padding: "16px 32px", display: "flex", gap: 12, flexShrink: 0 }}>
        {[
          { label: "Variables extracted", value: insights.length.toString() },
          { label: "Ready for pipeline", value: readyCount.toString(), sub: `of ${insights.length}` },
          { label: "Needs review", value: reviewCount.toString() },
          { label: "Avg certainty", value: avgCertainty ? `${(avgCertainty * 100).toFixed(0)}%` : "-" },
          { label: "Avg predictive power", value: avgPredictive ? `${(avgPredictive * 100).toFixed(0)}%` : "-" },
        ].map(card => (
          <div key={card.label} className="card" style={{ flex: 1, padding: "16px 20px", borderRadius: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.4)", marginBottom: 6 }}>
              {card.label}
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: 28, fontWeight: 300, letterSpacing: "-0.03em" }}>{card.value}</span>
              {card.sub && <span style={{ fontSize: 12, color: "rgba(0,0,0,0.35)" }}>{card.sub}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Variable cards list */}
      <div style={{ flex: 1, overflow: "auto", padding: "0 32px 80px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 900 }}>
          {insights.map((ins, idx) => {
            const varName = getVarName(ins, idx);
            const isExpanded = expandedVar === idx;
            const status = statuses[idx] || "needs_review";
            const sc = statusColors[status];
            const tc = typeColors[ins.type] || typeColors.variable;
            const typeLabel = ins.type === "variable" ? "Continuous" : ins.type === "belief" ? "Estimate" : "Threshold";

            return (
              <div key={idx} style={{
                border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, background: "#fff",
                overflow: "hidden", transition: "all 0.2s ease",
                opacity: status === "excluded" ? 0.5 : 1,
                ...(isExpanded ? { borderColor: "rgba(0,0,0,0.18)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" } : {}),
              }}>
                {/* Row header — collapsed view */}
                <div
                  onClick={() => { setExpandedVar(isExpanded ? null : idx); setActiveSection("overview"); }}
                  style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{varName}</span>
                      <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 8px", borderRadius: 20, background: sc.bg, color: sc.text }}
                        onClick={e => { e.stopPropagation(); cycleStatus(idx); }}
                      >{sc.label}</span>
                      <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 8px", borderRadius: 20, background: "rgba(99,102,241,0.1)", color: "#6366f1" }}>elicitation</span>
                      <span style={{ fontSize: 9, fontWeight: 500, padding: "1px 8px", borderRadius: 20, background: tc.bg, color: tc.text }}>{typeLabel}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", marginTop: 3, lineHeight: 1.4 }}>
                      {(ins.text || ins.mechanism || "").slice(0, 120)}{(ins.text || ins.mechanism || "").length > 120 ? "..." : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                    {ins.prior_mean != null && ins.prior_std != null && (
                      <span style={{ fontSize: 10, color: "rgba(99,102,241,0.8)", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>
                        N({ins.prior_mean.toFixed(2)}, {ins.prior_std.toFixed(2)})
                      </span>
                    )}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round"
                      style={{ opacity: 0.25, transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ padding: "0 16px 16px", animation: "fadeSlideIn 0.3s ease" }}>
                    {/* Section tabs */}
                    <div style={{ display: "flex", gap: 4, marginBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.06)", paddingBottom: 8 }}>
                      {["overview", "verbatim", "prior", "boundaries"].map(sec => (
                        <button key={sec} onClick={() => setActiveSection(sec)} style={{
                          padding: "4px 12px", borderRadius: 6, border: "none",
                          fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
                          cursor: "pointer", fontFamily: "inherit",
                          background: activeSection === sec ? "#000" : "rgba(0,0,0,0.04)",
                          color: activeSection === sec ? "#fff" : "rgba(0,0,0,0.45)",
                          transition: "all 0.15s ease",
                        }}>{sec}</button>
                      ))}
                    </div>

                    {/* Overview section */}
                    {activeSection === "overview" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(0,0,0,0.65)" }}>
                          {ins.text || ins.mechanism || "No description available"}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                          {[
                            ["Type", typeLabel],
                            ["Target", "Hospitalization_Rate"],
                            ["Domain", "Public Health"],
                            ["Owner", expertName],
                            ["Source", `Expert elicitation (${expertName})`],
                            ["Created", today],
                            ["Updated", today],
                            ["Status", sc.label],
                          ].map(([label, val]) => (
                            <div key={label as string} style={{ background: "rgba(0,0,0,0.03)", borderRadius: 8, padding: "8px 12px" }}>
                              <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
                              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{val}</div>
                            </div>
                          ))}
                        </div>

                        {/* Mechanism summary */}
                        {ins.mechanism && (
                          <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(0,0,0,0.03)" }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.4)", marginBottom: 4 }}>
                              Mechanism (Claude Summary)
                            </div>
                            <div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(0,0,0,0.65)" }}>
                              {ins.mechanism}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Verbatim section — direct expert quotes */}
                    {activeSection === "verbatim" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {ins.source_quote ? (
                          <div style={{
                            padding: "14px 18px", borderRadius: 8,
                            background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.1)",
                            borderLeft: "4px solid rgba(99,102,241,0.4)",
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(99,102,241,0.7)", marginBottom: 6 }}>
                              Verbatim — Expert Quote
                            </div>
                            <div style={{
                              fontSize: 13, lineHeight: 1.7, color: "rgba(0,0,0,0.7)",
                              fontStyle: "italic",
                            }}>
                              "{ins.source_quote}"
                            </div>
                            <div style={{ fontSize: 10, color: "rgba(0,0,0,0.35)", marginTop: 8 }}>
                              — {expertName}
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: "20px", textAlign: "center", color: "rgba(0,0,0,0.3)", fontSize: 12 }}>
                            No verbatim quote captured for this variable
                          </div>
                        )}

                        {/* Claude's interpretation for comparison */}
                        {ins.mechanism && (
                          <div style={{
                            padding: "14px 18px", borderRadius: 8,
                            background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.06)",
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.35)", marginBottom: 6 }}>
                              Claude's Summary
                            </div>
                            <div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(0,0,0,0.55)" }}>
                              {ins.mechanism}
                            </div>
                          </div>
                        )}

                        {/* Description / text */}
                        {ins.text && ins.text !== ins.mechanism && ins.text !== ins.source_quote && (
                          <div style={{
                            padding: "14px 18px", borderRadius: 8,
                            background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.06)",
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.35)", marginBottom: 6 }}>
                              Description
                            </div>
                            <div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(0,0,0,0.55)" }}>
                              {ins.text}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Prior section */}
                    {activeSection === "prior" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {/* Prior distribution card */}
                        <div style={{ padding: "14px 18px", borderRadius: 8, background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.1)" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(99,102,241,0.7)", marginBottom: 8 }}>
                            Expert Prior
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                            {[
                              ["Distribution", "Normal"],
                              ["Mean (μ)", ins.prior_mean != null ? ins.prior_mean.toFixed(4) : "-"],
                              ["Std (σ)", ins.prior_std != null ? ins.prior_std.toFixed(4) : "-"],
                              ["Certainty", ins.confidence ? `${(ins.confidence * 100).toFixed(0)}%` : "-"],
                            ].map(([label, val]) => (
                              <div key={label as string} style={{
                                background: "rgba(99,102,241,0.06)", borderRadius: 8, padding: "8px 12px",
                                border: "1px solid rgba(99,102,241,0.08)",
                              }}>
                                <div style={{ fontSize: 9, color: "rgba(99,102,241,0.6)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2, fontFamily: "'IBM Plex Mono', monospace" }}>{val}</div>
                              </div>
                            ))}
                          </div>

                          {/* Prior visualization */}
                          {ins.prior_mean != null && ins.prior_std != null && (
                            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
                              <div style={{ flex: 1, position: "relative", height: 32 }}>
                                <div style={{ position: "absolute", top: 14, left: 0, right: 0, height: 2, background: "rgba(99,102,241,0.15)", borderRadius: 1 }} />
                                <div style={{
                                  position: "absolute", top: 10, height: 10, borderRadius: 5,
                                  background: "rgba(99,102,241,0.25)",
                                  left: `${Math.max(0, 50 - 20)}%`, width: "40%",
                                }} />
                                <div style={{ position: "absolute", top: 6, width: 2, height: 18, background: "#6366f1", left: "50%", borderRadius: 1 }} />
                                <div style={{
                                  position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
                                  fontSize: 9, fontWeight: 700, color: "#6366f1", fontFamily: "'IBM Plex Mono', monospace",
                                }}>{ins.prior_mean.toFixed(2)}</div>
                                <div style={{
                                  position: "absolute", bottom: 0, left: `${Math.max(0, 50 - 20)}%`,
                                  fontSize: 8, color: "rgba(99,102,241,0.5)", fontFamily: "'IBM Plex Mono', monospace",
                                }}>{(ins.prior_mean - ins.prior_std).toFixed(2)}</div>
                                <div style={{
                                  position: "absolute", bottom: 0, right: `${Math.max(0, 50 - 20)}%`,
                                  fontSize: 8, color: "rgba(99,102,241,0.5)", fontFamily: "'IBM Plex Mono', monospace",
                                  textAlign: "right",
                                }}>{(ins.prior_mean + ins.prior_std).toFixed(2)}</div>
                              </div>
                              <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", minWidth: 80 }}>
                                ±1σ range<br />
                                <strong style={{ color: "#6366f1" }}>
                                  [{(ins.prior_mean - ins.prior_std).toFixed(2)}, {(ins.prior_mean + ins.prior_std).toFixed(2)}]
                                </strong>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Predictive power */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          <div style={{ background: "rgba(0,0,0,0.03)", borderRadius: 8, padding: "10px 14px" }}>
                            <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Predictive Power</div>
                            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, fontFamily: "'IBM Plex Mono', monospace" }}>
                              {ins.predictive_power ? `${(ins.predictive_power * 100).toFixed(0)}%` : "-"}
                            </div>
                          </div>
                          <div style={{ background: "rgba(0,0,0,0.03)", borderRadius: 8, padding: "10px 14px" }}>
                            <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Expert Certainty</div>
                            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, fontFamily: "'IBM Plex Mono', monospace" }}>
                              {ins.confidence ? `${(ins.confidence * 100).toFixed(0)}%` : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Boundaries section — thresholds + boundary conditions */}
                    {activeSection === "boundaries" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {/* Thresholds */}
                        {ins.thresholds && ins.thresholds.length > 0 ? (
                          <div style={{ padding: "14px 18px", borderRadius: 8, background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.1)" }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#d97706", marginBottom: 8 }}>
                              Thresholds & Tipping Points
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              {ins.thresholds.map((t, i) => (
                                <div key={i} style={{
                                  display: "flex", alignItems: "flex-start", gap: 8,
                                  padding: "6px 10px", borderRadius: 6, background: "rgba(245,158,11,0.06)",
                                }}>
                                  <span style={{ fontSize: 10, color: "#d97706", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>T{i + 1}</span>
                                  <span style={{ fontSize: 12, color: "rgba(0,0,0,0.65)", lineHeight: 1.5 }}>{t}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: "14px 18px", borderRadius: 8, background: "rgba(0,0,0,0.02)", textAlign: "center", color: "rgba(0,0,0,0.3)", fontSize: 12 }}>
                            No thresholds identified
                          </div>
                        )}

                        {/* Boundary conditions */}
                        {ins.boundary_conditions && ins.boundary_conditions.length > 0 ? (
                          <div style={{ padding: "14px 18px", borderRadius: 8, background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.1)" }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#dc2626", marginBottom: 8 }}>
                              Boundary Conditions — When This Breaks Down
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              {ins.boundary_conditions.map((b, i) => (
                                <div key={i} style={{
                                  display: "flex", alignItems: "flex-start", gap: 8,
                                  padding: "6px 10px", borderRadius: 6, background: "rgba(239,68,68,0.04)",
                                }}>
                                  <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>!</span>
                                  <span style={{ fontSize: 12, color: "rgba(0,0,0,0.65)", lineHeight: 1.5 }}>{b}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: "14px 18px", borderRadius: 8, background: "rgba(0,0,0,0.02)", textAlign: "center", color: "rgba(0,0,0,0.3)", fontSize: 12 }}>
                            No boundary conditions identified
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action bar */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 16, padding: "0 4px", maxWidth: 900,
        }}>
          <span style={{ fontSize: 11, color: "rgba(0,0,0,0.35)" }}>
            {insights.length} variables · Click to expand · Click status to cycle
          </span>
          <button
            className="btn btn-filled"
            style={{ height: 38, padding: "0 24px", fontSize: 12, fontWeight: 500 }}
          >
            Load {readyCount} into Sandbox
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
